/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState, memo } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import {
  setBusy,
  clearBusy,
} from '../../../irmlib/src/containers'
import { useTranslation } from 'react-i18next'
import { Box, Typography, Grid } from '@mui/material'
import * as localGdeTransactions from '../../../services/firestore/localGdeTransactions'
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table'
import { useTheme } from 'irmlib/styles'
import { useHistory } from 'react-router-dom'
import * as routePaths from '../../../navigation/routePaths'
import { errorToast } from '../../../common/functions'
import ExportCsv from '../../Controls/ExportCsv'

const BottomPaddedDiv = styled('div')`
  padding-bottom: 20px;
`

const ImagePara = styled('p')`
  color: ${props => props.theme.palette.primary.main};
  text-decoration: none;
  cursor : pointer;
`
const ColoredPara = styled.p(props => ({
  color: props.isPriority ? 'red' : '' 
}))

const TableContainer = styled(BottomPaddedDiv)``

const ViewQualityCheckImagesData = memo((props) => {
  const value = props.FilterData
  const handleSearchDisabled = props.handleSearchDisabled
  const { t } = useTranslation()
  const [transmittalData, setData] = useState([])
  const theme = useTheme()
  const history = useHistory()

  useEffect(() => {
    let unsubscribeObjects
    getQualityCheckImageRecords().then(v => {
      unsubscribeObjects = v
    })
    return () => {
      const { unsubscribeAssignedToMe, unsubscribeIdlImages } = unsubscribeObjects
      if (unsubscribeAssignedToMe) {
        unsubscribeAssignedToMe()
      }
      if (unsubscribeIdlImages) {
        unsubscribeIdlImages()
      }
    }
  }, [value])


  const getQualityCheckImageRecords = async () => {
    let unsubscribe
    try {
      setBusy()
      const requiredImageFields = ['requestId', 'district', 'building', 'accountLevel1', 'onHold', 'activityId', 'skpBoxNbr', 'imgId', 'isLocked', 'lockedBy', 'createTS']
      const imageSnapListnerRes = await localGdeTransactions.getLocalQcFailedImagesListenerSnap(value, setData, requiredImageFields)
      unsubscribe = imageSnapListnerRes
      handleSearchDisabled(true)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error fetching page data:', err)
    } finally {
      clearBusy()
    }
    return unsubscribe
  }


  const handleOnImageIdClicked = async (imgId, activityId, requestId, skpBoxNbr, documentID) => {
    try {
      setBusy()
      await localGdeTransactions.lockAndAsignQualityCheckImagesToUser(documentID)
      // if the trnaction completed without error push to QC page
      history.push({
        pathname: `${routePaths.localQualityCheck}`,
        state: {
          fromAdmin: false,
          imageID: imgId,
          imageActivityId: activityId,
          imageRequestId: requestId,
          imageSkpBoxNbr: skpBoxNbr,
          imageDocId: documentID
        }
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      const msg = error?.message === 'Locked Image Error' ? 'Image is already locked' : 'Error while feteching'
      errorToast({ message: msg, autoHideDuration: 3000, isBlocking: false })
      // eslint-disable-next-line no-console
      console.error('Error fetching page data', error)
      return []
    } finally {
      clearBusy()
    }
  }

  // all the logic for material table will check the feasibility and move to a sperate componet later if required.
  const displayTableColums = useMemo(() => [
    // {
    //   header: t('demo.requestId'),
    //   accessorKey: 'requestId',
    // },
    {
      header: t('demo.requestId'),
      accessorKey: 'requestId',
      Cell: ({ row }) => {
        const { isLocked, requestId, onHold } = row?.original
        return <ColoredPara isPriority={isLocked || onHold}>{requestId}</ColoredPara>
      }
    },
    {
      header: t('dataEntry.customerId'),
      accessorKey: 'accountLevel1',
    },
    // {
    //   header: t('tableColumns.district'),
    //   accessorKey: 'district',
    // },
    {
      header: t('templates.buildings'),
      accessorKey: 'building',
    },
    {
      header: t('tableColumns.boxNumber'),
      accessorKey: 'skpBoxNbr',
    },
    {
      header: t('tableColumns.imageId'),
      accessorKey: 'imgId',
      Cell: ({ row }) => {
        const { imgId, activityId, requestId, skpBoxNbr, documentID } = row?.original
        return (
          <ImagePara
            id={`myJobs_jobLink_${documentID}`}
            onClick={() => handleOnImageIdClicked(imgId, activityId, requestId, skpBoxNbr, documentID)}
            theme={theme}>
            {t('smartCatalogLocal.viewImage')}
          </ImagePara>
        )
      }
    },
    {
      header: t('tableColumns.isLocked'),
      accessorKey: 'isLocked',
      Cell: ({ row }) => {
        const { isLocked } = row?.original
        return <ColoredPara isPriority={isLocked}>{isLocked ? 'Yes' : 'No'}</ColoredPara>
      }
    },
    // {
    //   header: t('tableColumns.lockedByUser'),
    //   accessorKey: 'lockedBy',
    //   Cell: ({ row }) => {
    //     const { lockedBy } = row?.original
    //     return lockedBy || ''
    //   }
    // },
    {
      header: 'On Hold',
      accessorKey: 'onHold',
      Cell: ({ row }) => {
        const { onHold } = row?.original
        return <ColoredPara isPriority={onHold}>{onHold ? 'Yes' : 'No'}</ColoredPara>
      }
    }
  ], [])


  const table = useMaterialReactTable({
    columns: displayTableColums,
    data: transmittalData,
    enableRowSelection: true,
    enableMultiRowSelection: true,
    enableExport: true,
    enableGlobalFilter: true,
    enableColumnFilter: true,
    enableColumnSorting: true,
    enableGrouping: true,
    enableColumnResizing: true,
    initialState: {
      sorting: [
        {
          id: 'isLocked',
          desc: true,
        },
        {
          id: 'onHold',
          desc: true
        },
        {
          id: 'skpBoxNbr',
          desc: true,
        },
      ],
      columnVisibility: { imgReference: true, modify: true },
      density: 'compact',
    },
    // muiTableBodyRowProps: ({ row }) => {
    // // conditionally style selected rows
    //   const { isLocked, onHold } = row.original
    //   return {
    //     sx: {
    //       color: isLocked ? 'red' : '',
    //     }
    //   }
    // },
    renderTopToolbarCustomActions: ({ table }) => {
      const isDisabled = !table.getIsSomeRowsSelected()
      const isNoData = transmittalData?.length === 0
      const isNoPageData = transmittalData?.length > 4
      return (
        <>
          <ExportCsv
            columns={displayTableColums}
            isDisabled={isDisabled}
            isNoData={isNoData}
            isNoPageData={isNoPageData}
            table={table}
            hideExportSelectedData={false}
            hideExportPageData={false}
          />
        </>
      )
    }
  })
  return (
    <>
      <TableContainer>
        <Box boxShadow={3} borderRadius={4}>
          <Grid item container sx={{ padding: '10px 15px 0px' }}>
            <Grid item container alignItems={'center'}>
              <Typography variant='h6' gutterBottom style={{ marginTop: '15px', color: '#58595B' }}>
                {t('routeTitles.viewQualityCheckImages')}
              </Typography>
            </Grid>
          </Grid>
          <MaterialReactTable
            table={table}
          />
        </Box>
      </TableContainer>
    </>
  )
})
ViewQualityCheckImagesData.propTypes = {
  FilterData: PropTypes.object
}

export default ViewQualityCheckImagesData
